import { QuoteDrawer } from '@/components/Quotes/QuoteDrawer';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import { useGetDrawerMarginStyles } from '@/hooks/useGetDrawerMarginStyles';
import { useParentDivLayoutStyles } from '@/hooks/useParentDivLayoutStyles';
import usePermissions from '@/hooks/usePermissions';
import { useGetAccountQuery } from '@/services/accounts';
import { useGetContractsQuery, useSendContractV2Mutation, useVoidContractMutation } from '@/services/contract';
import { useFetchWaterHeaterAccountPricingMutation } from '@/services/pricing';
import { useCreateQuoteMutation, useGetQuotesQuery, useVoidQuoteMutation } from '@/services/quotes';
import {
  useCreateSystemDesignMutation,
  useGetCurrentDesignQuery,
  useLazyGetCurrentDesignQuery,
} from '@/services/systemDesign';
import { Alert, Box, Button, Modal, Spinner, toast, useOpenClose } from '@palmetto/palmetto-components';
import { Formik, FormikErrors } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ContractStatus,
  EquipmentPricingDetails,
  ProgramType,
  QuoteStatus,
  UserPermissions,
  WaterHeaterDiscountItem,
  WaterHeaterEquipmentItem,
  WaterHeaterEquipmentType,
  WaterHeaterPreliminaryEquipmentItem,
  WaterHeaterSitePrepItem,
  WaterHeaterSystem,
  WaterHeaterTankCapacityRanges,
  WaterHeaterTanklessFlowRateRanges,
  WaterHeaterType,
  WaterHeaterTypeLabels,
} from 'types';

import { useIsOrgReadOnly } from '../../auth/claimsUtils';
import RequirePermissions from '../../auth/requirePermissions';
import { EnhancedFormikForm } from '../../EnhancedFormikForm';
import { MainContainer } from '../../MainContainer';
import { calculateWaterHeaterSystemDesignTotalCost } from '../../Quotes/WaterHeaterQuoteCalculator.utils';

import { SystemSection } from './SystemSection';

const formatSystemData = (values: any, isPreliminary: boolean) => {
  return values.systems.map((system: any) => {
    const equipmentItems = isPreliminary
      ? system.equipment.items.map((item: WaterHeaterPreliminaryEquipmentItem) => ({
          type: item.type,
          quantity: 1,
          fuelType: item.fuelType,
          waterHeaterType: item.waterHeaterType,
          tankCapacity: item.tankCapacity,
          tanklessFlowRate: item.tanklessFlowRate,
        }))
      : system.equipment.items.map((item: WaterHeaterEquipmentItem) => ({
          type: item.type,
          manufacturer: item.manufacturer,
          model: item.model,
          quantity: 1,
          serialNumbers: item.serialNumbers,
          fuelType: item.fuelType,
          waterHeaterType: item.waterHeaterType,
          tankCapacity: item.tankCapacity,
          tanklessFlowRate: item.tanklessFlowRate,
        }));

    return {
      name: system.name,
      ahriNumber: system.ahriNumber,
      equipment: {
        totalCost: system.equipment.totalCost,
        items: equipmentItems,
      },
      sitePrep: system?.sitePrep
        ? {
            totalCost: system.sitePrep.totalCost,
            items: system.sitePrep.items.map((item: WaterHeaterSitePrepItem) => ({
              name: item.name,
            })),
          }
        : undefined,
      discounts: system?.discounts
        ? {
            totalCost: system.discounts.totalCost,
            items: system.discounts.items.map((item: WaterHeaterDiscountItem) => ({
              name: item.name,
            })),
          }
        : undefined,
    };
  });
};

interface WaterHeaterSystemFormData extends Omit<WaterHeaterSystem, 'equipment'> {
  equipment: {
    items: (WaterHeaterEquipmentItem | WaterHeaterPreliminaryEquipmentItem)[];
    totalCost?: number | string;
  };
}

interface WaterHeaterFormValues {
  systems: WaterHeaterSystemFormData[];
}

const validateWaterHeaterForm = (
  values: WaterHeaterFormValues,
  isPreliminary: boolean,
): FormikErrors<WaterHeaterFormValues> => {
  const errors: Record<string, any> = {};

  values.systems.forEach((system, systemIndex) => {
    // Equipment total cost required (can be 0 but must be present)
    if (system.equipment?.totalCost == null || system.equipment.totalCost === '') {
      if (!errors.systems) errors.systems = [];
      if (!errors.systems[systemIndex]) errors.systems[systemIndex] = {};
      if (!errors.systems[systemIndex].equipment) errors.systems[systemIndex].equipment = {};
      errors.systems[systemIndex].equipment.totalCost = 'Equipment total cost is required';
    }

    const items = system.equipment?.items || [];

    if (items.length === 0) {
      if (!errors.systems) errors.systems = [];
      if (!errors.systems[systemIndex]) errors.systems[systemIndex] = {};
      errors.systems[systemIndex].equipment = { items: 'At least one equipment item is required' };
      return;
    }

    items.forEach((item: any, itemIndex: number) => {
      const itemErrors: any = {};
      const typeLabel = item.waterHeaterType
        ? WaterHeaterTypeLabels[item.waterHeaterType as WaterHeaterType] || item.waterHeaterType
        : `Water Heater ${itemIndex + 1}`;

      if (!item.fuelType) {
        itemErrors.fuelType = `Fuel type is required for ${typeLabel}`;
      }
      if (!item.waterHeaterType) {
        itemErrors.waterHeaterType = `Water heater type is required for Water Heater ${itemIndex + 1}`;
      }

      if (item.waterHeaterType === WaterHeaterType.tank || item.waterHeaterType === WaterHeaterType.heatPump) {
        if (!item.tankCapacity?.value) {
          itemErrors.tankCapacity = `Tank capacity is required for ${typeLabel}`;
        }
      }

      if (item.waterHeaterType === WaterHeaterType.tankless) {
        if (!item.tanklessFlowRate?.value) {
          itemErrors.tanklessFlowRate = `Flow rate is required for ${typeLabel}`;
        }
      }

      if (!isPreliminary) {
        if (!item.manufacturer) {
          itemErrors.manufacturer = `Manufacturer is required for ${typeLabel}`;
        }
        if (!item.model) {
          itemErrors.model = `Model is required for ${typeLabel}`;
        }
      }

      if (Object.keys(itemErrors).length > 0) {
        if (!errors.systems) errors.systems = [];
        if (!errors.systems[systemIndex]) errors.systems[systemIndex] = {};
        if (!errors.systems[systemIndex].equipment) errors.systems[systemIndex].equipment = {};
        if (!errors.systems[systemIndex].equipment.items) errors.systems[systemIndex].equipment.items = [];
        errors.systems[systemIndex].equipment.items[itemIndex] = itemErrors;
      }
    });
  });

  return errors;
};

const flattenErrors = (errors: FormikErrors<WaterHeaterFormValues>, values: WaterHeaterFormValues): string[] => {
  const messages: string[] = [];
  const systems = errors.systems as any[] | undefined;
  if (!systems) return messages;

  systems.forEach((systemErrors: any, systemIndex: number) => {
    if (!systemErrors) return;
    const systemName = values.systems[systemIndex]?.name || `System ${systemIndex + 1}`;
    const prefix = systemName;

    if (systemErrors.equipment) {
      if (typeof systemErrors.equipment === 'string') {
        messages.push(`${prefix} Equipment: ${systemErrors.equipment}`);
      } else {
        if (systemErrors.equipment.totalCost) {
          messages.push(`${prefix} Equipment: ${systemErrors.equipment.totalCost}`);
        }
        if (typeof systemErrors.equipment.items === 'string') {
          messages.push(`${prefix} Equipment: ${systemErrors.equipment.items}`);
        } else if (Array.isArray(systemErrors.equipment.items)) {
          const equipmentItems = values.systems[systemIndex]?.equipment?.items || [];
          systemErrors.equipment.items.forEach((itemErrors: any, itemIndex: number) => {
            if (!itemErrors) return;
            const item = equipmentItems[itemIndex] as any;
            const typeLabel = item?.waterHeaterType
              ? WaterHeaterTypeLabels[item.waterHeaterType as WaterHeaterType] || item.waterHeaterType
              : `Water Heater ${itemIndex + 1}`;
            Object.values(itemErrors).forEach((msg) => {
              if (typeof msg === 'string') {
                messages.push(`${prefix} Equipment — ${typeLabel}: ${msg}`);
              }
            });
          });
        }
      }
    }
  });

  return messages;
};

export const WaterHeaterSystemDesign = () => {
  const targetRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { isOpen: isDrawerOpen, handleOpen: openDrawer, handleClose: closeDrawer } = useOpenClose();

  const parentStyles = useParentDivLayoutStyles(targetRef);
  const { id } = useParams<{ id: any }>();
  const [createQuote] = useCreateQuoteMutation();
  const drawerMarginStyles = useGetDrawerMarginStyles(parentStyles, isDrawerOpen);

  const { data: systemDesign } = useGetCurrentDesignQuery(
    {
      id,
      programType: ProgramType.waterHeater,
    },
    { skip: !id },
  );
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id, { skip: !id });
  const programType = account?.programType;
  const { data: quotes, isLoading: isQuotesLoading } = useGetQuotesQuery(
    { id, programType },
    { skip: !programType || !id },
  );
  const [voidQuote, { isLoading: isVoidingQuote }] = useVoidQuoteMutation({
    fixedCacheKey: 'useVoidQuoteMutationKey',
  });
  const { data: contracts, isLoading: areContractsLoading } = useGetContractsQuery(id, { skip: !id });
  const [fetchPricing, { data: pricingData = [], isLoading: isPricingLoading, status: pricingStatus }] =
    useFetchWaterHeaterAccountPricingMutation();

  const isLoading = isAccountLoading || isQuotesLoading || areContractsLoading;
  const [createSystemDesign, { isLoading: isCreatingSystemDesign }] = useCreateSystemDesignMutation();
  const [getCurrentSystemDesign, { data: createdSystemDesign }] = useLazyGetCurrentDesignQuery();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const {
    isOpen: isQuoteCreationModalOpen,
    handleOpen: openQuoteCreationModal,
    handleClose: closeQuoteCreationModal,
  } = useOpenClose();
  const { comfortPlanDealerConditions } = useFlags();
  const userPermissions = usePermissions();
  const [sendContract, { isLoading: isSendingContract }] = useSendContractV2Mutation({
    fixedCacheKey: 'useSendContractV2MutationKey',
  });
  const [formValues, setFormValues] = useState<any>({});
  const [createdQuote, setCreatedQuote] = useState<any>(null);
  const allowsAction = useIsOrgReadOnly();
  const [isPreliminary, setIsPreliminary] = useState(true);

  useEffect(() => {
    if (systemDesign) {
      setIsPreliminary(systemDesign.isPreliminary);
    }
  }, [systemDesign]);

  const activeContract = useMemo(() => {
    return contracts?.find(
      (contract: any) => ![ContractStatus.voided, ContractStatus.rejected].includes(contract.status),
    );
  }, [contracts]);

  const [voidContract, { isLoading: isVoidingActiveContract }] = useVoidContractMutation({
    fixedCacheKey: 'useVoidContractMutationKey',
  });

  const systemDesignCost = useMemo(() => {
    if (!createdSystemDesign && !systemDesign) {
      return 0;
    }
    return calculateWaterHeaterSystemDesignTotalCost(createdSystemDesign || systemDesign);
  }, [createdSystemDesign, systemDesign]);

  const activeQuote = quotes?.find((quote: any) => quote.status === QuoteStatus.active);

  const calculateTotalCost = useCallback((values: { systems: WaterHeaterSystem[] }) => {
    const totalCost = calculateWaterHeaterSystemDesignTotalCost(values as any) || 0;

    return totalCost;
  }, []);

  const toggleDrawer = () => {
    if (isDrawerOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  };

  const handleSubmit = async (
    data: {
      systems: WaterHeaterSystem[];
    },
    { setSubmitting }: any,
  ) => {
    try {
      setFormValues(data);
      const totalSystemDesignCost = calculateTotalCost(data);
      if (activeQuote && totalSystemDesignCost !== activeQuote.totalSystemCost) {
        openModal();
        return;
      }
      await createSystemDesign({
        systemDesign: {
          isPreliminary,
          systems: formatSystemData(data, isPreliminary),
        },
        accountId: id,
        programType: programType as ProgramType,
      }).unwrap();
      toast.success('System design saved successfully');
      getCurrentSystemDesign({
        id,
        programType: ProgramType.waterHeater,
      });
      await fetchPricing({
        accountId: id,
        useCurrentSystemDesign: true,
      });
      openDrawer();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Error saving system design');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmModalSubmit = async () => {
    closeModal();
    if (formValues) {
      try {
        if (activeContract) {
          await voidContract({
            accountId: String(id),
            contractId: String(activeContract.id),
          }).unwrap();
          toast.success('Active Contract voided successfully');
        }
        await voidQuote({ accountId: id.toString(), quoteId: activeQuote.id.toString() }).unwrap();
        toast.success('Quote voided successfully');
        await createSystemDesign({
          systemDesign: {
            isPreliminary,
            systems: formatSystemData(formValues, isPreliminary),
          },
          accountId: id,
          programType: programType as ProgramType,
        }).unwrap();
        toast.success('System design saved successfully');
        getCurrentSystemDesign({
          id,
          programType: ProgramType.waterHeater,
        });
        await fetchPricing({
          accountId: id,
          useCurrentSystemDesign: true,
        });
        openDrawer();
      } catch (e: any) {
        toast.error(e?.data?.message || 'Error saving system design');
      }
    }
  };

  const isVoiding = isVoidingQuote || isVoidingActiveContract;

  const ratesAndTerms = useMemo(() => {
    if (!pricingData || !pricingData.length) {
      return { rates: [], terms: [] };
    }

    const rates = Array.from(new Set(pricingData.map((item: any) => item.escalationRate)))
      .toSorted((a: any, b: any) => a - b)
      .map((rate: any) => ({
        label: `${(rate * 100).toFixed(2)}%`,
        value: rate.toString(),
        id: rate.toString(),
      }));
    const terms = Array.from(new Set(pricingData.map((item: any) => item.term)))
      .toSorted((a: any, b: any) => a - b)
      .map((term: any) => ({
        label: `${term} years`,
        value: term.toString(),
        id: term.toString(),
      }));
    return { rates, terms };
  }, [pricingData]);

  const handleCreateQuote = async (values: any) => {
    const selectedProduct = pricingData.find(
      (product: any) => product.term === parseInt(values.term) && product.escalationRate === parseFloat(values.rate),
    );

    if (!selectedProduct) {
      toast.error('No matching pricing found for selected term and rate');
      return;
    }

    const creationPayload = {
      quoteData: {
        productId: selectedProduct.productId,
        externalReference: account?.externalReference || undefined,
        totalFinancedAmount: systemDesignCost,
      },
      id: account.id,
      programType: ProgramType.waterHeater,
    } as any;
    try {
      const quote = await createQuote(creationPayload).unwrap();
      const userIsAllowedToSendContract =
        comfortPlanDealerConditions && userPermissions.includes(UserPermissions.comfortPlanDealer);
      if (userIsAllowedToSendContract && quote?.status === QuoteStatus.active) {
        setCreatedQuote(quote);
        openQuoteCreationModal();
      } else {
        navigate(`/accounts/${id}`);
        toast.success('Quote created successfully');
      }
    } catch (e) {
      toast.error('Error creating quote');
      console.error(e);
    }
  };

  const handleSendContract = useCallback(async () => {
    const toastId = toast.loading('Sending contract');
    try {
      if (activeContract?.quoteId === activeQuote?.id) {
        await sendContract({
          accountId: String(activeContract?.accountId),
          contractId: String(activeContract?.id),
          forceSend: true,
        }).unwrap();
        navigate(`/accounts/${id}`);
        toast.success('Contract sent successfully');
      } else {
        toast.error('Unable to send contract: contract and active quote do not match');
      }
    } catch (e: any) {
      if (e?.data?.errors) {
        toast.error(e?.data?.errors.join(', '));
      } else {
        toast.error(e?.data?.message || 'Error sending contract');
      }
    } finally {
      toast.dismiss(toastId);
    }
  }, [activeContract, activeQuote, sendContract, navigate, id]);

  const handleCloseQuoteCreationModal = useCallback(() => {
    closeQuoteCreationModal();
    navigate(`/accounts/${id}`);
  }, [closeQuoteCreationModal, navigate, id]);

  const initialValues = useMemo(() => {
    const tankCapacityOptions = WaterHeaterTankCapacityRanges.map((range) => ({
      label: range.label,
      value: range.value,
    }));
    const tanklessFlowRateOptions = WaterHeaterTanklessFlowRateRanges.map((range) => ({
      label: range.label,
      value: range.value,
    }));

    if (!systemDesign || !systemDesign?.systems?.length) {
      const systemsToCreate = Array.from({ length: account?.propertyDetails?.systemsBeingReplaced || 1 });
      return {
        systems: systemsToCreate.map((_, index: number) => ({
          systemCategory: '',
          name: `System ${index + 1}`,
          equipment: {
            totalCost: 0,
            items: [
              {
                type: WaterHeaterEquipmentType.waterHeater,
                quantity: 1,
              },
            ],
          },
          sitePrep: {
            totalCost: 0,
            items: [],
          },
          discounts: {
            totalCost: 0,
            items: [],
          },
        })),
      };
    }

    // Transform equipment items to include select field values for dropdowns
    const transformedSystems = systemDesign.systems.map((system: WaterHeaterSystem) => ({
      ...system,
      equipment: {
        ...system.equipment,
        items: system.equipment.items.map(
          (item: WaterHeaterEquipmentItem | WaterHeaterPreliminaryEquipmentItem) => ({
            ...item,
            tankCapacitySelect: item.tankCapacity?.value
              ? tankCapacityOptions.find((opt) => opt.value === item.tankCapacity?.value)
              : undefined,
            tanklessFlowRateSelect: item.tanklessFlowRate?.value
              ? tanklessFlowRateOptions.find((opt) => opt.value === item.tanklessFlowRate?.value)
              : undefined,
          }),
        ),
      },
    }));

    return {
      ...systemDesign,
      systems: transformedSystems,
    };
  }, [systemDesign, account]);

  return (
    <>
      <MainContainer>
        {isLoading ? (
          <Box display="block" textAlign="center" padding="lg" childGap="xl">
            <Spinner size="lg" />
          </Box>
        ) : (
          <Box
            overflow="hidden"
            childGap="lg"
            ref={targetRef}
            style={{
              marginRight: drawerMarginStyles,
              position: 'relative',
              transition: 'margin-right 0.3s ease',
            }}
          >
            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              enableReinitialize={true}
              validateOnChange={true}
              validateOnMount={true}
              validate={(values) => validateWaterHeaterForm(values as WaterHeaterFormValues, isPreliminary)}
            >
              {({ values, isSubmitting, errors, submitCount }) => {
                const totalSystemDesignCost = calculateTotalCost(values);
                return (
                  <EnhancedFormikForm noValidate>
                    {account?.propertyDetails && (
                      <Box gap="lg">
                        <Box
                          borderColor="separator"
                          borderWidth="0 0 xs 0"
                          padding={{ desktop: 'lg 0 xl 0', base: 'lg 3xl' }}
                          direction="row"
                          childGap="5xl"
                          fontSize="md"
                          fontWeight="medium"
                        >
                          <Box>Account Details</Box>
                          <Box childGap="sm" padding={{ base: '0 0 0 5xl' }}>
                            {account?.propertyDetails?.homeSize && (
                              <Box>Home Size (sq ft): {account?.propertyDetails?.homeSize}</Box>
                            )}
                            {account?.propertyDetails?.existingSystems && (
                              <Box>Existing Systems: {account?.propertyDetails?.existingSystems}</Box>
                            )}
                            {account?.propertyDetails?.systemsBeingReplaced && (
                              <Box>Systems Being Installed: {account?.propertyDetails?.systemsBeingReplaced}</Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}
                    <SystemSection isPreliminary={isPreliminary} />
                    <Box alignItems="flex-end" margin="md" padding={{ base: 'lg 0 0 0' }} gap="xs">
                      <Box direction="row" gap="xs" alignItems="center">
                        <Box fontWeight="medium" fontSize="md">
                          Total Number of Systems:
                        </Box>
                        <Box fontWeight="bold" fontSize="lg">
                          {values.systems.length}
                        </Box>
                      </Box>
                      <Box direction="row" gap="xs" alignItems="center">
                        <Box fontWeight="medium" fontSize="md">
                          Total System Design Cost:
                        </Box>
                        <Box fontWeight="bold" fontSize="lg">
                          {currencyFormatter.format(totalSystemDesignCost)}
                        </Box>
                      </Box>
                    </Box>
                    {submitCount > 0 && Object.keys(errors).length > 0 && (
                      <Box padding="sm 0">
                        <Alert
                          variant="danger"
                          hasIcon
                          title="Please fix the following errors:"
                          render={() => (
                            <Box as="ul" gap="xs" fontSize="sm">
                              {flattenErrors(
                                errors as FormikErrors<WaterHeaterFormValues>,
                                values as WaterHeaterFormValues,
                              ).map((error, i) => (
                                <li key={i}>{error}</li>
                              ))}
                            </Box>
                          )}
                        />
                      </Box>
                    )}
                    <Box
                      direction={{
                        base: 'column',
                        tablet: 'row',
                      }}
                      childGap="sm"
                      gap="lg"
                      style={{ flexShrink: 0 }}
                      padding={{ base: 'lg' }}
                      background="primary"
                      wrap={true}
                    >
                      {activeQuote ? (
                        <Box alignSelf="flex-start" direction="row" gap="lg">
                          <Box gap="xs">
                            <Box fontSize="xl" fontWeight="bold">
                              {currencyFormatter.format(activeQuote.totalSystemCost)}
                            </Box>
                            <Box fontSize="md">Total Financed Amount</Box>
                          </Box>
                          <Box gap="xs">
                            <Box direction="row" alignItems="center" gap="2xs">
                              <Box fontSize="xl" fontWeight="bold">
                                {currencyFormatter.format(
                                  activeQuote.systemPricingDetails.find((d: EquipmentPricingDetails) => d.year === 1)
                                    ?.monthlyPayment || 0,
                                )}
                              </Box>
                              <Box fontSize="md">/mo</Box>
                            </Box>
                            <Box fontSize="md">Year 1 Monthly Payment</Box>
                          </Box>
                          <Box gap="xs">
                            <Box direction="row" alignItems="center" gap="2xs">
                              <Box fontSize="xl" fontWeight="bold">
                                {activeQuote.systemPricingDetails.length}
                              </Box>
                              <Box fontSize="md">
                                {activeQuote.systemPricingDetails.length > 1 ? `years` : `year`}
                              </Box>
                            </Box>
                            <Box fontSize="md">Customer Term Length</Box>
                          </Box>
                        </Box>
                      ) : null}
                      <RequirePermissions
                        permissions={[UserPermissions.admin, UserPermissions.editor]}
                        checkAllPermissions={false}
                      >
                        <Box style={{ marginLeft: 'auto' }} direction="row" gap="sm">
                          <Button
                            as="a"
                            href={`/accounts/${id ? id : ''}`}
                            variant="secondary"
                            tone="neutral"
                            size="md"
                            isDisabled={isSubmitting || isCreatingSystemDesign || isPricingLoading || isVoiding}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="md"
                            variant="primary"
                            type="submit"
                            isDisabled={isSubmitting || isCreatingSystemDesign || isPricingLoading || isVoiding}
                            isLoading={isSubmitting || isCreatingSystemDesign || isPricingLoading || isVoiding}
                          >
                            Save & Get Pricing
                          </Button>
                        </Box>
                      </RequirePermissions>
                    </Box>
                  </EnhancedFormikForm>
                );
              }}
            </Formik>
            {!pricingData.length && pricingStatus !== 'uninitialized' && !isPricingLoading ? (
              <Alert variant="info" message="No pricing options available for this system design" />
            ) : null}
            <QuoteDrawer
              isOpen={isDrawerOpen}
              toggleDrawer={toggleDrawer}
              ratesAndTerms={ratesAndTerms}
              handleCreateQuote={handleCreateQuote}
              isLoading={isPricingLoading || isCreatingSystemDesign || isVoiding}
              systemDesignCost={systemDesignCost}
              isPricingLoading={isPricingLoading}
              pricingData={pricingData}
              programType={ProgramType.waterHeater}
            />
          </Box>
        )}
      </MainContainer>

      <Modal ariaLabelledBy="costMismatch" isOpen={isModalOpen} onDismiss={closeModal}>
        <Modal.Header id="costMismatch" title="Cost Mismatch" onDismiss={closeModal} />
        <Modal.Body>
          The total system design cost does not match the active quote cost. Would you like to void the existing
          quote {activeContract ? 'and active contract' : ''} and create a new one?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" tone="neutral" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmModalSubmit} disabled={!allowsAction}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        ariaLabelledBy="quoteCreationModal"
        isOpen={isQuoteCreationModalOpen}
        onDismiss={handleCloseQuoteCreationModal}
      >
        <Modal.Header
          id="quoteCreationModal"
          title="Quote Created Successfully"
          onDismiss={handleCloseQuoteCreationModal}
        />
        <Modal.Body>
          <Box gap="md" direction="column">
            <Box display="block">
              You can now send the contract to the customer for signature. To send them their contract for a{' '}
              {<strong>{createdQuote?.systemPricingDetails?.length}</strong>}{' '}
              {(createdQuote?.systemPricingDetails?.length ?? 0) > 1 ? 'years' : 'year'} Comfort Plan with an Initial
              Monthly Payment of{' '}
              {
                <strong>
                  {currencyFormatter.format(
                    createdQuote?.systemPricingDetails?.find((d: EquipmentPricingDetails) => d.year === 1)
                      ?.monthlyPayment ?? 0,
                  )}
                </strong>
              }
              , click the button below.
            </Box>
            <Box display="block">
              The customer will also be invited to create their Palmetto Account where they must complete all the
              Notice to Proceed steps. The customer must complete all steps in their account in order to achieve NTP.
              Our chat support team is standing by to assist you!
            </Box>
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleSendContract} disabled={isSendingContract}>
            Send Contract
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
